import { message } from 'antd';
import { AxiosInstance } from 'axios';

import {
  ITEM_COMPARISON_STATUS,
  PackageItemComparison,
} from '@/model/CaseResolution/PackageItemComparison';
import { PackageItem } from '@/model/Logistic/PackageItem/PackageItem';
import {
  PACKAGE_ITEM_RESOLUTION,
  PACKAGE_ITEM_STOCK_RESOLUTION,
} from '@/model/Logistic/PackageItem/PackageItemResolution';
import { RECEIVED_PRODUCT_STATUS } from '@/model/Logistic/PackageItem/ReceivedProductStatus';
import { PACKAGE_STATES } from '@/model/Logistic/PackageStates';
import { RETURN_ITEM_TYPE } from '@/model/Logistic/Reception/ReturnItemType';
import { SHIPPING_ITEM_REFERENCE } from '@/model/Logistic/Shipment/ShipmentReference';

import PackageItemApi from './PackageItemApi';

class PackageItemRepository {
  packageItemApi: PackageItemApi;

  constructor(axiosInstance: AxiosInstance) {
    this.packageItemApi = new PackageItemApi(axiosInstance);
  }

  savePackageItems = async (packageItemsData: PackageItem[], packageId: number) => {
    try {
      const packageItems = packageItemsData.map(item => ({
        ...item,
        package: packageId,
        return_type:
          item.is_returned == undefined
            ? undefined
            : item.is_returned
              ? item.return_type
              : RETURN_ITEM_TYPE.SOLICITUD_REPARACION,
      }));
      const promises: Promise<any>[] = [];

      packageItems.forEach(item => {
        if (!item.id && !item.toDelete) {
          promises.push(this.packageItemApi.createPackageItem(item));
        } else if (item.id && item.toDelete) {
          promises.push(this.packageItemApi.deletePackageItem(item));
        } else if (item.id && item.toUpdate) {
          promises.push(this.packageItemApi.updatePackageItem(item));
        }
      });
      await Promise.all(promises);
    } catch (error) {
      console.error('Error saving package items:', error);
      throw error;
    }
  };

  updatePackageItem = async (id, packageItemData: Partial<PackageItem>) => {
    try {
      const response = await this.packageItemApi.updatePackageItem({ id, ...packageItemData });

      return response.data;
    } catch (error) {
      console.error('Error updating package item:', error);
      message.error('Error al actualizar el item');
      throw error;
    }
  };

  getPackageItemsFromPackages(packages: number[]): Promise<PackageItem[]> {
    return Promise.all(packages.map(packageId => this.packageItemApi.getPackageItems(packageId)))
      .then(results => {
        const packageItemsArrays = results.map(response => response.results);
        const packageItems = packageItemsArrays.flat();

        return packageItems;
      })
      .catch(error => {
        console.error('Error fetching package items from packages:', error);
        message.error('Error al obtener los items del paquete');
        throw error;
      });
  }

  async getShippedItemsFromTickets(tickets: any) {
    try {
      const response = await this.packageItemApi.getShippedItemsFromTickets(tickets);

      if (Array.isArray(response)) {
        return response;
      }

      if (response?.results) {
        return response.results;
      }

      return response?.data ?? [];
    } catch (error) {
      console.error('Error fetching shipped items from tickets:', error);
      message.error('Error al obtener los items enviados de los tickets');
      throw error;
    }
  }

  /**
   * Metadata:
   * - Proposito: centralizar la validacion de items activos para resolucion de casos.
   * - Regla: replica exactamente la condicion existente
   *   (package embebido y no cancelado).
   */
  private isItemFromActivePackage(item: PackageItem): boolean {
    return typeof item.package != 'number' && item.package?.status != PACKAGE_STATES.CANCELADO;
  }

  /**
   * Metadata:
   * - Proposito: emular el comportamiento de `find` en O(1) para relaciones
   *   `item_shipment_related -> receptionItem`.
   * - Regla: conserva el primer match encontrado segun el orden original.
   */
  private buildReceptionByShipmentId(
    receptionItems: PackageItem[],
  ): Map<number | undefined, PackageItem> {
    const relationMap = new Map<number | undefined, PackageItem>();

    for (const receptionItem of receptionItems) {
      const shipmentId = receptionItem.item_shipment_related;

      if (!relationMap.has(shipmentId)) {
        relationMap.set(shipmentId, receptionItem);
      }
    }

    return relationMap;
  }

  /**
   * Metadata:
   * - Proposito: emular el comportamiento de `find` en O(1) para relaciones
   *   `item_reception_related -> shipmentItem`.
   * - Regla: conserva el primer match encontrado segun el orden original.
   */
  private buildShipmentByReceptionId(
    shipmentItems: PackageItem[],
  ): Map<number | undefined, PackageItem> {
    const relationMap = new Map<number | undefined, PackageItem>();

    for (const shipmentItem of shipmentItems) {
      const receptionId = shipmentItem.item_reception_related;

      if (!relationMap.has(receptionId)) {
        relationMap.set(receptionId, shipmentItem);
      }
    }

    return relationMap;
  }

  /**
   * Metadata:
   * - Proposito: encapsular la regla de estado de comparacion para envios con recepcion.
   * - Regla: mantiene la misma priorizacion y condiciones que la implementacion previa.
   */
  private resolveRoundTripStatus(
    shipment: PackageItem,
    relatedReception: PackageItem | undefined,
    sent: number,
    received: number,
    technicianStock: number,
    receivedStatus: RECEIVED_PRODUCT_STATUS | undefined,
    receivedResolution: PACKAGE_ITEM_RESOLUTION | undefined,
  ): ITEM_COMPARISON_STATUS {
    let status: ITEM_COMPARISON_STATUS = ITEM_COMPARISON_STATUS.CORRECT;
    const { item_shipped_reference } = shipment;

    if (receivedResolution != undefined) {
      if (receivedResolution == PACKAGE_ITEM_RESOLUTION.RESOLUCION_AUTOMATICA) {
        status = ITEM_COMPARISON_STATUS.CORRECT;
      } else {
        status = ITEM_COMPARISON_STATUS.RESOLVED;
      }
    } else if (
      !relatedReception ||
      item_shipped_reference == SHIPPING_ITEM_REFERENCE.SUJETO_PERITAJE ||
      receivedStatus == RECEIVED_PRODUCT_STATUS.A_REVISION_REPARACION ||
      receivedStatus == RECEIVED_PRODUCT_STATUS.NO_CONFORME ||
      sent - received - technicianStock != 0 ||
      shipment.serial_number != relatedReception.serial_number
    ) {
      status = ITEM_COMPARISON_STATUS.REVIEW;
    } else if (receivedStatus == RECEIVED_PRODUCT_STATUS.RECIBIDO_OK) {
      status = ITEM_COMPARISON_STATUS.CORRECT;
    } else {
      status = ITEM_COMPARISON_STATUS.REVIEW;
    }

    return status;
  }

  /**
   * Metadata:
   * - Proposito: encapsular la regla de estado base para items de tecnico.
   * - Regla: replica la logica actual sin cambios funcionales.
   */
  private resolveTechnicianStatus(
    receivedResolution: PACKAGE_ITEM_RESOLUTION | undefined,
  ): ITEM_COMPARISON_STATUS {
    let status: ITEM_COMPARISON_STATUS = ITEM_COMPARISON_STATUS.REVIEW;

    if (receivedResolution != undefined) {
      if (receivedResolution == PACKAGE_ITEM_RESOLUTION.RESOLUCION_AUTOMATICA) {
        status = ITEM_COMPARISON_STATUS.CORRECT;
      } else {
        status = ITEM_COMPARISON_STATUS.RESOLVED;
      }
    }

    return status;
  }

  /**
   * Metadata:
   * - Proposito: aplicar la regla de revision de stock sobre el estado ya calculado.
   * - Regla: solamente eleva a `REVIEW_STOCK` si el estado era CORRECT/RESOLVED
   *   y el stock aun figura NO_RESUELTO.
   */
  private applyStockReviewStatus(
    status: ITEM_COMPARISON_STATUS,
    stockResolution: PACKAGE_ITEM_STOCK_RESOLUTION,
  ): ITEM_COMPARISON_STATUS {
    if (
      (status == ITEM_COMPARISON_STATUS.CORRECT || status == ITEM_COMPARISON_STATUS.RESOLVED) &&
      stockResolution == PACKAGE_ITEM_STOCK_RESOLUTION.NO_RESUELTO
    ) {
      return ITEM_COMPARISON_STATUS.REVIEW_STOCK;
    }

    return status;
  }

  buildPackageItemRoundTripComparisons(
    shipmentItems: PackageItem[],
    receptionItems: PackageItem[],
    technicianItems: PackageItem[],
  ): PackageItemComparison[] {
    const comparisons: PackageItemComparison[] = [];
    const receptionByShipmentId = this.buildReceptionByShipmentId(receptionItems);

    for (const shipment of shipmentItems) {
      const { item_shipped_reference } = shipment;

      if (
        this.isItemFromActivePackage(shipment) &&
        (item_shipped_reference == SHIPPING_ITEM_REFERENCE.SUJETO_PERITAJE ||
          item_shipped_reference == SHIPPING_ITEM_REFERENCE.PREVENTIVO)
      ) {
        const relatedReception = receptionByShipmentId.get(shipment.id);
        const sent = shipment.execute_amount ?? 0;
        const received = relatedReception?.execute_amount ?? 0;
        const used = relatedReception?.used_amount ?? 0;
        const discarded = relatedReception?.discarded_amount ?? 0;
        const technicianStock = relatedReception?.technician_stock_amount ?? 0;
        const receivedStatus = relatedReception?.item_received_state;
        const receivedResolution = relatedReception?.resolution_status;
        const receivedStockResolution =
          relatedReception?.stock_resolution_status ?? PACKAGE_ITEM_STOCK_RESOLUTION.NO_RESUELTO;
        const baseStatus = this.resolveRoundTripStatus(
          shipment,
          relatedReception,
          sent,
          received,
          technicianStock,
          receivedStatus,
          receivedResolution,
        );
        const status = this.applyStockReviewStatus(baseStatus, receivedStockResolution);

        comparisons.push({
          id: `${shipment.id}${relatedReception ? '-' + relatedReception.id : ''}`,
          productId:
            typeof shipment.product === 'object' ? shipment.product.id : (shipment.product ?? 0),
          productName: shipment.name ?? 'Producto desconocido',
          productType: shipment.product_type,
          productStatus: shipment.item_shipped_state,
          shipmentItem: shipment,
          receptionItem: relatedReception,
          usedAmount: used,
          discardedAmount: discarded,
          technicianStockAmount: technicianStock,
          sentAmount: sent,
          receivedAmount: received,
          shipmentReference: shipment.item_shipped_reference,
          returnItemType: relatedReception?.return_type,
          status,
          resolution_status: receivedResolution,
          stock_resolution_status: receivedStockResolution,
        });
      }
    }

    for (const technicianItem of technicianItems) {
      // Los items que vuelven de técnico se resuelven como "sujeto a peritaje",
      // pero sin un item de envío asociado.
      if (this.isItemFromActivePackage(technicianItem)) {
        const received = technicianItem.execute_amount ?? 0;
        const used = technicianItem.used_amount ?? 0;
        const discarded = technicianItem.discarded_amount ?? 0;
        const technicianStock = technicianItem.technician_stock_amount ?? 0;
        const receivedResolution = technicianItem.resolution_status;
        const receivedStockResolution =
          technicianItem.stock_resolution_status ?? PACKAGE_ITEM_STOCK_RESOLUTION.NO_RESUELTO;
        const baseStatus = this.resolveTechnicianStatus(receivedResolution);
        const status = this.applyStockReviewStatus(baseStatus, receivedStockResolution);

        comparisons.push({
          id: `technician-${technicianItem.id}`,
          productId:
            typeof technicianItem.product === 'object'
              ? technicianItem.product.id
              : (technicianItem.product ?? 0),
          productName: technicianItem.name ?? 'Producto desconocido',
          productType: technicianItem.product_type,
          productStatus: technicianItem.item_shipped_state,
          shipmentItem: undefined,
          receptionItem: technicianItem,
          usedAmount: used,
          discardedAmount: discarded,
          technicianStockAmount: technicianStock,
          sentAmount: 0,
          receivedAmount: received,
          shipmentReference: SHIPPING_ITEM_REFERENCE.SUJETO_PERITAJE,
          returnItemType: technicianItem.return_type,
          status,
          resolution_status: receivedResolution,
          stock_resolution_status: receivedStockResolution,
        });
      }
    }

    return comparisons;
  }

  buildPackageItemReceptionComparisons(
    receptionItems: PackageItem[],
    shipmentItems: PackageItem[],
  ): PackageItemComparison[] {
    // Comparison for reparation/revision requests are built
    const receptionsForRevisionComparisons: PackageItemComparison[] = [];
    const shipmentByReceptionId = this.buildShipmentByReceptionId(shipmentItems);

    receptionItems.forEach(receptionItem => {
      const relatedShipment = shipmentByReceptionId.get(receptionItem.id);

      receptionsForRevisionComparisons.push({
        id: `${relatedShipment ? relatedShipment.id : 'unknown'}-${
          receptionItem ? receptionItem.id : 'unknown'
        }`,
        productId:
          typeof receptionItem.product === 'object'
            ? receptionItem.product.id
            : (receptionItem.product ?? 0),
        productName: receptionItem.name ?? 'Producto desconocido',
        productType: receptionItem.product_type,
        productStatus: receptionItem.item_shipped_state,
        shipmentItem: relatedShipment,
        receptionItem: receptionItem,
        usedAmount: receptionItem.used_amount ?? 0,
        discardedAmount: receptionItem.discarded_amount ?? 0,
        technicianStockAmount: receptionItem.technician_stock_amount ?? 0,
        sentAmount: relatedShipment?.execute_amount ?? 0,
        receivedAmount: receptionItem.execute_amount ?? 0,
        shipmentReference: relatedShipment?.item_shipped_reference ?? undefined,
        returnItemType: receptionItem.return_type,
        status: ITEM_COMPARISON_STATUS.REVIEW,
        resolution_status: receptionItem.resolution_status,
        stock_resolution_status: receptionItem.stock_resolution_status,
      });
    });

    return receptionsForRevisionComparisons;
  }

  /**
   * Retorna los elementos para mostrar en la resolucion de casos
   * @param packageItems
   * @returns objeto { itemComparisons, shipmentItems, receptionItems } donde:
   * - itemComparisons es el listado de comparaciones entre items de envío y recepción sujetos a peritaje o preventivos
   * - shipmentItems es el listado de items de envío para venta y sin devolución
   * - receptionItems es el listado de items de recepción para reparación/revisión
   */
  buildPackageItemsForResolution(packageItems: PackageItem[]): {
    itemComparisons: PackageItemComparison[];
    nonReturnItems: PackageItem[];
    receptionForRevisionComparisons: PackageItemComparison[];
  } {
    const shipmentItems = packageItems.filter(item => item.is_shipment_item);
    const receptionItems = packageItems.filter(item => item.is_reception_item);
    const technicianItems = packageItems.filter(item => item.is_technician_item);
    const activeShipmentItems = shipmentItems.filter(item => this.isItemFromActivePackage(item));
    const itemComparisons = this.buildPackageItemRoundTripComparisons(
      shipmentItems,
      receptionItems,
      technicianItems,
    );
    const nonReturnItems = activeShipmentItems.filter(
      item =>
        item.item_shipped_reference != SHIPPING_ITEM_REFERENCE.SUJETO_PERITAJE &&
        item.item_shipped_reference != SHIPPING_ITEM_REFERENCE.PREVENTIVO,
    );
    const filteredReceptionItems = receptionItems.filter(
      item =>
        !item.is_technician_item &&
        item.return_type == RETURN_ITEM_TYPE.SOLICITUD_REPARACION &&
        this.isItemFromActivePackage(item),
    );
    const receptionForRevisionComparisons = this.buildPackageItemReceptionComparisons(
      filteredReceptionItems,
      nonReturnItems,
    );

    return {
      itemComparisons,
      nonReturnItems,
      receptionForRevisionComparisons,
    };
  }
}

export default PackageItemRepository;
